import { useEffect, useState } from 'react';
import safetySystemService from '../services/safetySystem.service';

let cached: boolean | null = null;
let inflight: Promise<boolean> | null = null;
const listeners = new Set<(v: boolean) => void>();

function load(): Promise<boolean> {
  if (cached !== null) return Promise.resolve(cached);
  if (!inflight) {
    inflight = safetySystemService
      .status()
      .then((s) => !!s.enabled)
      .catch(() => false)
      .then((v) => {
        cached = v;
        listeners.forEach((l) => l(v));
        return v;
      });
  }
  return inflight;
}

/**
 * Three-state release flag: `null` until the status call resolves, then the
 * backend SAFETY_SYSTEM_ENABLED value (any fetch error = `false`). Resolved once
 * per page load and shared across every hook instance. Route gates need the
 * `null` state so a direct load of /safety/hub does not bounce before the flag
 * is known.
 */
export function useSafetySystemFlag(): boolean | null {
  const [flag, setFlag] = useState<boolean | null>(cached);
  useEffect(() => {
    listeners.add(setFlag);
    load().then(setFlag);
    return () => {
      listeners.delete(setFlag);
    };
  }, []);
  return flag;
}

/** True only when the release flag is on (unresolved counts as off). */
export function useSafetySystemEnabled(): boolean {
  return useSafetySystemFlag() ?? false;
}

/** Test-only: clear the module-level cache between tests. */
export function __resetSafetySystemCache(): void {
  cached = null;
  inflight = null;
  listeners.clear();
}
